import type { GeneratedDefinition } from "@keydials/api-client";

// ---------------------------------------------------------------------------
// What the builder has selected (KEY-560), by keys rather than positions. The wheel and the readiness check both speak in
// keys, the builder never changes a key once it exists, and a selection has to survive the reflow when something is added or
// removed around it. Keys are only unique within their parent, so an action point or item carries the whole chain; a field's key
// is unique within its level, so a field carries its level.
// ---------------------------------------------------------------------------

/** A section, action point or item: a place in the dial's structure. */
export type NodeSelection =
  | { level: "section"; sectionKey: string }
  | { level: "segment"; sectionKey: string; segmentKey: string }
  | { level: "item"; sectionKey: string; segmentKey: string; itemKey: string };

/** One of the details the dial asks for (a node attribute), on Details. */
export type FieldSelection = { level: "field"; target: string; key: string };

export type BuilderSelection = NodeSelection | FieldSelection;

/** A structure selection placed in the current model, with its indexes for rendering. */
export interface ResolvedSelection {
  selection: NodeSelection;
  sectionIndex: number;
  segmentIndex?: number;
  itemIndex?: number;
}

/** A field selection placed in the current model, with its index into `nodeAttributes`. */
export interface ResolvedFieldSelection {
  selection: FieldSelection;
  fieldIndex: number;
}

/**
 * Where a structure selection sits in the model now. A node that has gone falls back to its nearest surviving parent, so deleting
 * the selected item leaves its action point selected. Null when there is no selection, its section has gone too, or it's a field,
 * which has no place in the structure (see {@link resolveFieldSelection}).
 */
export function resolveSelection(model: GeneratedDefinition, selection: BuilderSelection | null): ResolvedSelection | null {
  if (!selection || selection.level === "field") return null;

  const sectionIndex = model.sections.findIndex((s) => s.key === selection.sectionKey);
  if (sectionIndex < 0) return null;
  const section = model.sections[sectionIndex];
  if (selection.level === "section") return { selection, sectionIndex };

  const segmentIndex = section.segments.findIndex((g) => g.key === selection.segmentKey);
  if (segmentIndex < 0) return { selection: { level: "section", sectionKey: section.key }, sectionIndex };
  if (selection.level === "segment") return { selection, sectionIndex, segmentIndex };

  const itemIndex = section.segments[segmentIndex].items.findIndex((i) => i.key === selection.itemKey);
  if (itemIndex < 0) {
    return { selection: { level: "segment", sectionKey: section.key, segmentKey: selection.segmentKey }, sectionIndex, segmentIndex };
  }
  return { selection, sectionIndex, segmentIndex, itemIndex };
}

/** Where a field selection sits in the model now: null when there is none, it isn't a field, or the field has gone. */
export function resolveFieldSelection(model: GeneratedDefinition, selection: BuilderSelection | null): ResolvedFieldSelection | null {
  if (selection?.level !== "field") return null;
  const fieldIndex = (model.nodeAttributes ?? []).findIndex((a) => a.target === selection.target && a.key === selection.key);
  return fieldIndex < 0 ? null : { selection, fieldIndex };
}

/**
 * What `<Dial>`'s `selection` prop shows for a builder selection: items aren't on the wheel, so an item selects its action point,
 * and a field selects nothing.
 */
export function toDialSelection(selection: BuilderSelection | null): { sectionKey: string; segmentKey?: string } | null {
  if (!selection || selection.level === "field") return null;
  return selection.level === "section"
    ? { sectionKey: selection.sectionKey }
    : { sectionKey: selection.sectionKey, segmentKey: selection.segmentKey };
}
